<template>
  <SlidingPanel :isPanelOpen="isSlidingPanelOpen" :isSubPanelOpen="isSubPanelOpen" @closePanel="isSlidingPanelOpen = false" @closeSubPanel="closeSubPanel">
    <template #content>
      <div class="sliding-panel-wrapper">
        <SpaceManagementView v-if="currentPanel === 'edit-space'" :space="selectedSpace" @openSubPanel="openSubPanel"></SpaceManagementView>
        <CreateSpacePanel v-else-if="currentPanel === 'create-space'" @close="closePanel"></CreateSpacePanel>
        <AddMemberPanel v-else :scope="{ kind: ScopeRefKind.WORKSPACE, id: selectedSpace.workspace_id }" @close="closeSubPanel"></AddMemberPanel>
      </div>
    </template>
  </SlidingPanel>
  <transition name="fade" mode="out-in">
    <div class="spaces-management-wrapper">
      <Header :name="$t('app.spaces.spacesView.header')"></Header>
      <div  v-if="!isSpacesPending && !isRolesPending" class="spaces-management-body">
        <div class="button-header">
          <Button v-if="canWriteSpaces" class="icon icon-left primary light-text m" :label="$t('app.spaces.spacesView.createSpace')" @click="currentPanel = 'create-space'; isSlidingPanelOpen = true">
            <template #prefix>
              <PlusIcon style="width: 16px;"></PlusIcon>
            </template>
          </Button>
          <VField :class="canWriteSpaces ? 'put-to-end' : undefined" class="light search m white-background" :placeholder="$t('app.campaign.single.search')" name="search" icon="bi-search" v-model="state.search.value" @input="(event) => (state.search.value = event.target.value)" />
        </div>
        <transition name="fade">
          <div class="spaces-table">
            <SpacesTable :search="state.search" @edit-space="editSpace"></SpacesTable>
          </div>
        </transition>
      </div>
      <SpaceModal :isOpen="state.modalIsOpen" :space="state.space" :action="state.modalAction" @close="((state.modalIsOpen = false), (state.space = null))" />
    </div>
  </transition>
</template>

<script setup lang="ts">
import { reactive } from "vue";
import { useRoute } from "vue-router";
import { useQueries } from "@tanstack/vue-query";

import Header from '@/components/Header.vue';
import SpaceModal from "@/components/modals/app/SpaceModal.vue";
import AddMemberPanel from "@/components/sliding-panel/AddMemberPanel.vue";
import SlidingPanel from "@/components/sliding-panel/SlidingPanel.vue";
import Button from "@/components/global/Button.vue";
import CreateSpacePanel from "@/components/sliding-panel/spaces/CreateSpacePanel.vue";
import SpacesTable from "@/components/global/tables/SpacesTable.vue";
import SpaceManagementView from "./SpaceManagementView.vue";

import PlusIcon from '@/assets/images/icon/campaign/plus.svg?component';

import { campaignsApi } from "@/api/campaigns.api";
import { Space } from "@/types/space";
import { ScopeRefKind } from "@/types/member.js";

import { useSpaces } from "@/queries/space.query";
import { useRoles } from "@/queries/user.query";

const route = useRoute();

const { canWriteSpaces } = usePermissions();
const { data: roles, isPending: isRolesPending } = useRoles();
const { data: spaces, isPending: isSpacesPending } = useSpaces();

const currentPanel: Ref<"edit-space" | "add-admin" | "create-space"> = ref(null);
const isSlidingPanelOpen = ref(false);
const isSubPanelOpen = ref(false);
const selectedSpace = ref<Space | null>(null);

function editSpace(space: Space) {
  currentPanel.value = "edit-space";
  selectedSpace.value = space;
  isSlidingPanelOpen.value = true;
}

function closePanel() {
  selectedSpace.value = null;
  isSlidingPanelOpen.value = false;
}

const state = reactive({
  modalAction: "",
  modalIsOpen: false,
  space: null,
  campaignsLoad: true,
  stats: [],
  search: {
    value: "",
    searchable: [
      "name",
    ],
  },
});

const queries = computed(() => {
  if (isSpacesPending.value) return [];
  return spaces.value.map((space) =>  {
    return {
      queryKey: ["campaigns", { organisationId: route.params.companyId as string, spaceId: space.workspace_id }],
      queryFn: () => campaignsApi.getAll(route.params.companyId as string, space.workspace_id, false),
    }
  });
});

const spaceCampaignsQueries = useQueries({ queries: queries });

const sortedSpaces = computed(() => {
  if (isSpacesPending.value) return [];
  return [...spaces.value].sort((a, b) => a.name.localeCompare(b.name));
});

function getNumberOfCampaigns(spaceId: string) {
  if (!spaceCampaignsQueries?.value || spaceCampaignsQueries?.value.length === 0) return 0;
  const campaigns = spaceCampaignsQueries.value.filter((v) => !v.isPending && v.data.some((v) => v.campaign.parent_workspace_id === spaceId)).flatMap((v) => v.data);
  return campaigns.length || 0;
}

function closeSubPanel() {
  isSubPanelOpen.value = false;
  isSlidingPanelOpen.value = false;

  setTimeout(() => {
    currentPanel.value = 'edit-space';
    isSlidingPanelOpen.value = true;
  }, 300);
}

function openSubPanel() {
  isSlidingPanelOpen.value = false;

  setTimeout(() => {
    currentPanel.value = 'add-admin';
    isSubPanelOpen.value = true;
    isSlidingPanelOpen.value = true;
  }, 300);
}
</script>

<style lang="scss" scoped>
@import "@/assets/style/variables.scss";

.row-text {
  max-width: 400px;
  text-overflow: ellipsis;
  overflow: hidden;
  white-space: nowrap
}

.sliding-panel-wrapper {
  display: flex;
  flex-direction: column;
  height: 100%;
}

.spaces-management-wrapper {
  display: flex;
  flex-direction: column;
  gap: 16px;
  flex: 1;
  overflow: hidden;
  max-width: 900px;
  width: 100%;
  margin: 0 auto;

  .spaces-management-body {
    height: 100%;
    display: flex;
    flex-direction: column;
    flex: 1;
    overflow: hidden;

    .button-header {
      display: flex;
      margin-bottom: 16px;

      .put-to-end {
        margin-left: auto;
      }

      .search {
        width: 350px;
      }
    }
  }

  .spaces-table {
    display: flex;
    flex-direction: column;
    flex: 1;
  }

  .center-field {
    justify-content: center !important;
  }

  .logo {
    width: 32px;
  }
}

</style>